import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { OwnerOption } from '../models/models';
import { OptionType } from '../constants/enums';
import api from './api';

interface OptionState {
  // Options vue tenant (toujours chargé) — source pour `useOption` quand pas de dept
  optionsTenant: OwnerOption[];
  // Options vue du département actif (cascade dept → owner → default)
  optionsDept:   OwnerOption[];
  currentDeptId: number | null;
  loading:        boolean;
  error:          string | null;
  successMessage: string | null;
}

const initialState: OptionState = {
  optionsTenant:  [],
  optionsDept:    [],
  currentDeptId:  null,
  loading:        false,
  error:          null,
  successMessage: null,
};

/**
 * Charge les options pour un niveau donné.
 * - departement_id null → vue tenant (stockée dans optionsTenant)
 * - departement_id set  → vue dept résolue (stockée dans optionsDept)
 */
export const fetchOptions = createAsyncThunk<
  { data: OwnerOption[]; departement_id: number | null },
  { departement_id?: number | null },
  { rejectValue: string }
>('options/fetchAll', async ({ departement_id = null }, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/options', {
      params: departement_id !== null ? { departement_id } : {},
    });
    return { data: (data.data ?? []) as OwnerOption[], departement_id };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec chargement des options');
  }
});

export const setOption = createAsyncThunk<
  { option_key: string },
  { option_key: string; value: any; departement_id?: number | null },
  { rejectValue: string }
>('options/set', async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    await api.post('/options/set', payload);
    // Recharge les vues pertinentes
    await dispatch(fetchOptions({ departement_id: null }));
    const currentDept = (getState() as any).OptionSlice.currentDeptId;
    if (currentDept !== null) await dispatch(fetchOptions({ departement_id: currentDept }));
    return { option_key: payload.option_key };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec mise à jour option');
  }
});

export const resetOption = createAsyncThunk<
  string,
  { option_key: string; departement_id?: number | null },
  { rejectValue: string }
>('options/reset', async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    await api.post('/options/reset', payload);
    await dispatch(fetchOptions({ departement_id: null }));
    const currentDept = (getState() as any).OptionSlice.currentDeptId;
    if (currentDept !== null) await dispatch(fetchOptions({ departement_id: currentDept }));
    return payload.option_key;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec reset option');
  }
});

/**
 * Reset massif des surcharges département.
 * option_key renseigné → pour cette option (bouton A)
 * option_key null/undefined → toutes les options (bouton B)
 */
export const resetAllDepts = createAsyncThunk<
  number,
  { option_key?: string | null },
  { rejectValue: string }
>('options/resetAllDepts', async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    const { data } = await api.post('/options/reset-all-depts', payload);
    await dispatch(fetchOptions({ departement_id: null }));
    const currentDept = (getState() as any).OptionSlice.currentDeptId;
    if (currentDept !== null) await dispatch(fetchOptions({ departement_id: currentDept }));
    return data.deleted_count ?? 0;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec reset global');
  }
});

const OptionSlice = createSlice({
  name: 'options',
  initialState,
  reducers: {
    clearMessages(state) {
      state.error          = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOptions.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchOptions.fulfilled, (state, action) => {
        state.loading = false;
        const { data, departement_id } = action.payload;
        if (departement_id === null) {
          state.optionsTenant = data;
        } else {
          state.optionsDept   = data;
          state.currentDeptId = departement_id;
        }
      })
      .addCase(fetchOptions.rejected,  (state, action) => { state.loading = false; state.error = action.payload ?? 'Erreur'; })
      .addCase(setOption.fulfilled,    (state) => { state.successMessage = 'Option mise à jour.'; })
      .addCase(setOption.rejected,     (state, action) => { state.error = action.payload ?? 'Erreur'; })
      .addCase(resetOption.fulfilled,  (state) => { state.successMessage = 'Option réinitialisée.'; })
      .addCase(resetOption.rejected,   (state, action) => { state.error = action.payload ?? 'Erreur'; })
      .addCase(resetAllDepts.fulfilled,(state, action) => { state.successMessage = `${action.payload} surcharge(s) supprimée(s).`; })
      .addCase(resetAllDepts.rejected, (state, action) => { state.error = action.payload ?? 'Erreur'; });
  },
});

// ── Parsing (miroir du backend) ──
export function parseOptionValue<T = any>(value: string | null | undefined, type: string, fallback?: T): T {
  if (value === null || value === undefined) return fallback as T;
  switch (type) {
    case OptionType.BOOLEAN: return (value === 'true' || value === '1') as unknown as T;
    case OptionType.NUMBER:  return Number(value) as unknown as T;
    case OptionType.JSON:
      try { return JSON.parse(value) as T; } catch { return fallback as T; }
    default:                  return value as unknown as T;
  }
}

export const { clearMessages } = OptionSlice.actions;
export default OptionSlice.reducer;
